import { useState } from "react";
import { useTranslation } from "react-i18next";
// components
import { Button } from "src/@/components/ui/button";
import { Card, CardContent } from "src/@/components/ui/card";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "src/@/components/ui/table";
import Iconify from "src/components/Iconify";
import ModalConfimacao from "src/components/modal/ModalConfimacao";
import SimpleDocumentUpload from "./SimpleDocumentUpload";
// actions
import v2_actionDeletarTreinamento from "src/actions/v2/ai/v2_actionDeletarTreinamento";
// utils
import { fDateTime } from "src/utils/formatTime";

// ----------------------------------------------------------------------

export default function FormDocuments({ agentId, trainings, onCallback }) {
    const { t } = useTranslation('settings');
    const [idTraining, setIdTraining] = useState('');
    const [loading, setLoading] = useState(false);
    const [openModalConfirmacao, setOpenModalConfirmacao] = useState(false);

    // ação de deletar
    async function deleteAnswer() {
        try {
            setLoading(true);
            await v2_actionDeletarTreinamento(idTraining);
            if (onCallback) onCallback();
            setOpenModalConfirmacao(false);
            setIdTraining('');
        } catch (error) {
            console.error('error', error);
        } finally {
            setLoading(false);
        }
    }

    const hasDocuments = trainings?.responseDocuments?.length > 0;

    return (
        <div className="flex flex-col h-full w-full gap-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <ModalConfimacao
                open={openModalConfirmacao}
                onClose={() => setOpenModalConfirmacao(false)}
                onConfirm={deleteAnswer}
                loadingFinalizar={loading}
                mensagem={t('aiAgents.training.formDocumentsDeleteConfirmMessage', 'Deseja mesmo excluir este documento de treinamento?')}
            />

            {/* Upload Area */}
            <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                <SimpleDocumentUpload agentId={agentId} onCallback={onCallback} />
            </div>

            {/* List Section */}
            {hasDocuments ? (
                <Card className="w-full bg-white border-gray-200 shadow-sm rounded-xl overflow-hidden">
                    <CardContent className="p-0">
                        <Table>
                            <TableHeader className="bg-gray-50">
                                <TableRow>
                                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500">{t('aiAgents.training.formDocumentsNameColumn', 'Nome')}</TableHead>
                                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500">{t('aiAgents.training.formDocumentsCreatedAtColumn', 'Data Criação')}</TableHead>
                                    <TableHead className="w-[100px]"></TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {trainings?.responseDocuments?.map((item) => (
                                    <TableRow key={item?._id} className="hover:bg-gray-50 transition-colors">
                                        <TableCell className="font-medium text-gray-700">
                                            <div className="flex items-center gap-2">
                                                <div className="p-2 bg-blue-50 rounded-lg text-blue-600">
                                                    <Iconify icon="solar:document-text-bold-duotone" width={20} />
                                                </div>
                                                <span className="capitalize">{item?.nameFile || t('aiAgents.training.formDocumentsFallbackName', 'Documento')}</span>
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-gray-500">
                                            {fDateTime(item?.createAt)}
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex justify-end">
                                                <Button
                                                    onClick={() => {
                                                        setIdTraining(item._id);
                                                        setOpenModalConfirmacao(true);
                                                    }}
                                                    variant="ghost"
                                                    size="icon"
                                                    className="text-gray-400 hover:text-red-600 hover:bg-red-50 transition-all rounded-lg"
                                                >
                                                    <Iconify icon="solar:trash-bin-trash-bold" width={20} />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            ) : (
                /* Empty State */
                <div className="flex flex-col items-center justify-center p-12 bg-white rounded-xl border border-dashed border-gray-200 text-center animate-in fade-in duration-500">
                    <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mb-4 text-gray-400">
                        <Iconify icon="solar:document-add-bold-duotone" width={32} />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">
                        {t('aiAgents.training.formDocumentsEmptyTitle', 'Nenhum documento encontrado')}
                    </h3>
                    <p className="text-sm text-gray-500 max-w-sm">
                        {t('aiAgents.training.formDocumentsEmptyDescription', 'Faça o upload de arquivos PDF, TXT ou DOCX para treinar seu agente com conhecimentos específicos.')}
                    </p>
                </div>
            )}
        </div>
    );
}